import { t as tr } from "@/lib/i18n";
import { useMemo } from "react";
import { FlowNodeKind, type FlowEdge, type FlowNode } from "@gen/tracker/v1/flow_pb";

const KIND_COLOR_VAR: Record<number, string> = {
  [FlowNodeKind.LANDER]: "--node-lander",
  [FlowNodeKind.OFFER]: "--node-offer",
  [FlowNodeKind.NETWORK]: "--node-network",
  [FlowNodeKind.FILTER]: "--node-filter",
  [FlowNodeKind.REDIRECT]: "--node-redirect",
  [FlowNodeKind.BRANCH]: "--node-branch",
  [FlowNodeKind.BOT_RULE]: "--node-bot-rule",
  [FlowNodeKind.SAFE_PAGE]: "--node-safe-page",
  [FlowNodeKind.JS_CHALLENGE]: "--node-js-challenge",
  [FlowNodeKind.FINGERPRINT_GATE]: "--node-fingerprint-gate",
};

export interface MiniBlueprintPreviewProps {
  nodes: FlowNode[];
  /** When provided (and non-empty), the preview renders the real DAG —
   *  BFS-layered from defaultNodeId — instead of a linear strip. */
  edges?: FlowEdge[];
  defaultNodeId?: string;
  className?: string;
  /** Vertical or horizontal? Default horizontal for cards. */
  orientation?: "vertical" | "horizontal";
}

interface LayoutNode {
  node: FlowNode;
  x: number;
  y: number;
}

const PILL_W = 38;
const PILL_H = 18;
const COL_GAP = 26;
const ROW_GAP = 8;
const MAX_LAYERS = 4;
const MAX_PER_LAYER = 3;

/**
 * MiniBlueprintPreview — tiny SVG overview for template cards / wizards.
 * With edges it paints the actual routing DAG (layers from the start node,
 * connectors between them); without edges it falls back to the legacy
 * linear pill strip. No interaction; pure paint.
 */
export function MiniBlueprintPreview({
  nodes,
  edges,
  defaultNodeId,
  className,
  orientation = "horizontal",
}: MiniBlueprintPreviewProps) {
  const dag = useMemo(() => {
    if (!edges || edges.length === 0 || !nodes || nodes.length === 0) return null;
    return layoutDag(nodes, edges, defaultNodeId);
  }, [nodes, edges, defaultNodeId]);

  if (!nodes || nodes.length === 0) {
    return (
      <div
        className={[
          "flex h-full w-full items-center justify-center text-xs text-muted-foreground",
          className ?? "",
        ].join(" ")}
      >
        {tr("bp.empty.tpl")}
      </div>
    );
  }

  if (dag) {
    return <DagPreview dag={dag} totalNodes={nodes.length} className={className} />;
  }
  return <LinearPreview nodes={nodes} className={className} orientation={orientation} />;
}

// layoutDag BFS-layers the routable graph from the start node. Condition
// nodes (FILTER referenced only via edge.condition_id) don't join the walk —
// matching how the click router treats them.
function layoutDag(
  nodes: FlowNode[],
  edges: FlowEdge[],
  defaultNodeId?: string,
): { placed: LayoutNode[]; links: Array<{ from: LayoutNode; to: LayoutNode; conditional: boolean }>; truncated: number } | null {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const adjacency = new Map<string, FlowEdge[]>();
  const hasIncoming = new Set<string>();
  for (const e of edges) {
    if (!byId.has(e.sourceId) || !byId.has(e.targetId)) continue;
    const list = adjacency.get(e.sourceId) ?? [];
    list.push(e);
    adjacency.set(e.sourceId, list);
    hasIncoming.add(e.targetId);
  }
  const start =
    (defaultNodeId && byId.has(defaultNodeId) && defaultNodeId) ||
    // A BRANCH root has no incoming edge either, but the ad click never
    // starts there — the overview paints the main journey.
    nodes.find(
      (n) => n.kind !== FlowNodeKind.BRANCH && adjacency.has(n.id) && !hasIncoming.has(n.id),
    )?.id ||
    nodes[0]?.id;
  if (!start) return null;

  // BFS layering.
  const layerOf = new Map<string, number>([[start, 0]]);
  const queue = [start];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    const layer = layerOf.get(cur)!;
    for (const e of adjacency.get(cur) ?? []) {
      if (!layerOf.has(e.targetId)) {
        layerOf.set(e.targetId, layer + 1);
        queue.push(e.targetId);
      }
    }
  }
  if (layerOf.size < 2) return null;

  const layers: FlowNode[][] = [];
  for (const [id, layer] of layerOf) {
    const capped = Math.min(layer, MAX_LAYERS - 1);
    (layers[capped] ??= []).push(byId.get(id)!);
  }
  let truncated = 0;
  const placed: LayoutNode[] = [];
  const pos = new Map<string, LayoutNode>();
  const maxRows = Math.min(
    Math.max(...layers.map((l) => Math.min(l?.length ?? 0, MAX_PER_LAYER))),
    MAX_PER_LAYER,
  );
  const height = maxRows * PILL_H + (maxRows - 1) * ROW_GAP + 12;
  layers.forEach((layer, li) => {
    const shown = layer.slice(0, MAX_PER_LAYER);
    truncated += layer.length - shown.length;
    const colHeight = shown.length * PILL_H + (shown.length - 1) * ROW_GAP;
    shown.forEach((node, ri) => {
      const ln: LayoutNode = {
        node,
        x: 8 + li * (PILL_W + COL_GAP),
        y: (height - colHeight) / 2 + ri * (PILL_H + ROW_GAP),
      };
      placed.push(ln);
      pos.set(node.id, ln);
    });
  });

  const links: Array<{ from: LayoutNode; to: LayoutNode; conditional: boolean }> = [];
  for (const e of edges) {
    const from = pos.get(e.sourceId);
    const to = pos.get(e.targetId);
    if (from && to) links.push({ from, to, conditional: !!e.conditionId });
  }
  return { placed, links, truncated };
}

function DagPreview({
  dag,
  totalNodes,
  className,
}: {
  dag: NonNullable<ReturnType<typeof layoutDag>>;
  totalNodes: number;
  className?: string;
}) {
  const viewW = Math.max(...dag.placed.map((p) => p.x + PILL_W)) + 8;
  const viewH = Math.max(...dag.placed.map((p) => p.y + PILL_H)) + 8;
  return (
    <svg
      viewBox={`0 0 ${viewW} ${viewH}`}
      preserveAspectRatio="xMidYMid meet"
      className={["max-h-24 w-full", className ?? ""].join(" ")}
      role="img"
      aria-label={tr("mini.graph", { n: totalNodes })}
    >
      {dag.links.map((l, i) => {
        const x1 = l.from.x + PILL_W;
        const y1 = l.from.y + PILL_H / 2;
        const x2 = l.to.x;
        const y2 = l.to.y + PILL_H / 2;
        const mx = (x1 + x2) / 2;
        return (
          <path
            key={i}
            d={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`}
            fill="none"
            stroke="var(--color-border)"
            strokeWidth="1.25"
            strokeDasharray={l.conditional ? "3 3" : undefined}
          />
        );
      })}
      {dag.placed.map((p) => (
        <Pill key={p.node.id} node={p.node} x={p.x} y={p.y} />
      ))}
      {dag.truncated > 0 ? (
        <text
          x={viewW - 4}
          y={viewH - 4}
          textAnchor="end"
          style={{ font: "500 8px Inter, sans-serif", fill: "var(--color-muted-foreground)" }}
        >
          +{dag.truncated}
        </text>
      ) : null}
    </svg>
  );
}

function Pill({ node, x, y }: { node: FlowNode; x: number; y: number }) {
  const colorVar = KIND_COLOR_VAR[node.kind] ?? "--color-primary";
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={PILL_W}
        height={PILL_H}
        rx="6"
        style={{
          fill: `color-mix(in oklch, var(${colorVar}), transparent 78%)`,
          stroke: `var(${colorVar})`,
          strokeWidth: 1,
        }}
      />
      <text
        x={x + PILL_W / 2}
        y={y + PILL_H / 2 + 1}
        textAnchor="middle"
        dominantBaseline="middle"
        style={{
          font: "600 8px Inter, sans-serif",
          fill: `var(${colorVar})`,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {kindShort(node.kind)}
      </text>
    </g>
  );
}

function LinearPreview({
  nodes,
  className,
  orientation,
}: {
  nodes: FlowNode[];
  className?: string;
  orientation: "vertical" | "horizontal";
}) {
  const sorted = [...nodes].sort((a, b) => a.sortOrder - b.sortOrder).slice(0, 6);
  const truncated = nodes.length > 6;
  const isHorizontal = orientation === "horizontal";

  const PILL = isHorizontal ? { w: 38, h: 18 } : { w: 76, h: 16 };
  const GAP = 12;

  if (isHorizontal) {
    const totalW = sorted.length * PILL.w + (sorted.length - 1) * GAP;
    const viewW = Math.max(totalW + 16, 120);
    const viewH = PILL.h + 32;
    const baselineY = viewH / 2;
    return (
      <svg
        viewBox={`0 0 ${viewW} ${viewH}`}
        preserveAspectRatio="xMidYMid meet"
        className={["w-full", className ?? ""].join(" ")}
        role="img"
        aria-label={tr("mini.graph", { n: nodes.length })}
      >
        <line
          x1={(viewW - totalW) / 2}
          y1={baselineY}
          x2={(viewW - totalW) / 2 + totalW}
          y2={baselineY}
          stroke="var(--color-border)"
          strokeWidth="1.25"
          strokeDasharray="2 4"
        />
        {sorted.map((n, i) => (
          <Pill
            key={n.id || i}
            node={n}
            x={(viewW - totalW) / 2 + i * (PILL.w + GAP)}
            y={baselineY - PILL.h / 2}
          />
        ))}
        {truncated ? (
          <text
            x={viewW - 10}
            y={baselineY}
            textAnchor="end"
            dominantBaseline="middle"
            style={{ font: "500 9px Inter, sans-serif", fill: "var(--color-muted-foreground)" }}
          >
            +{nodes.length - 6}
          </text>
        ) : null}
      </svg>
    );
  }

  const totalH = sorted.length * PILL.h + (sorted.length - 1) * GAP;
  const viewW = PILL.w + 32;
  const viewH = totalH + 16;
  const baselineX = viewW / 2;
  return (
    <svg
      viewBox={`0 0 ${viewW} ${viewH}`}
      preserveAspectRatio="xMidYMid meet"
      className={["w-full", className ?? ""].join(" ")}
      role="img"
    >
      <line
        x1={baselineX}
        y1={(viewH - totalH) / 2}
        x2={baselineX}
        y2={(viewH - totalH) / 2 + totalH}
        stroke="var(--color-border)"
        strokeWidth="1.25"
        strokeDasharray="2 4"
      />
      {sorted.map((n, i) => {
        const y = (viewH - totalH) / 2 + i * (PILL.h + GAP);
        const colorVar = KIND_COLOR_VAR[n.kind] ?? "--color-primary";
        return (
          <g key={n.id || i}>
            <rect
              x={baselineX - PILL.w / 2}
              y={y}
              width={PILL.w}
              height={PILL.h}
              rx="6"
              style={{
                fill: `color-mix(in oklch, var(${colorVar}), transparent 78%)`,
                stroke: `var(${colorVar})`,
                strokeWidth: 1,
              }}
            />
            <text
              x={baselineX}
              y={y + PILL.h / 2 + 1}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{
                font: "600 8px Inter, sans-serif",
                fill: `var(${colorVar})`,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              {kindShort(n.kind)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function kindShort(kind: FlowNodeKind): string {
  switch (kind) {
    case FlowNodeKind.LANDER:
      return "LAND";
    case FlowNodeKind.OFFER:
      return "OFFER";
    case FlowNodeKind.NETWORK:
      return "NET";
    case FlowNodeKind.FILTER:
      return "IF";
    case FlowNodeKind.REDIRECT:
      return "GO→";
    case FlowNodeKind.BRANCH:
      return "BR";
    case FlowNodeKind.BOT_RULE:
      return "BOT";
    case FlowNodeKind.SAFE_PAGE:
      return "SAFE";
    case FlowNodeKind.JS_CHALLENGE:
      return "JS";
    case FlowNodeKind.FINGERPRINT_GATE:
      return "FP";
    default:
      return "?";
  }
}
